import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFileContract, FaEnvelope, FaExclamationTriangle } from 'react-icons/fa';

const LAST_UPDATED = 'March 1, 2026';
const COMPANY = 'Fitnession Health Tech Pvt. Ltd.';
const EMAIL = 'legal@fitnession.fit';

const sections = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'medical-nature', title: '2. Medical Nature of Program' },
  { id: 'eligibility', title: '3. Eligibility Requirements' },
  { id: 'telemedicine', title: '4. Telemedicine Consent' },
  { id: 'prescription', title: '5. Prescription & Medication' },
  { id: 'subscriptions', title: '6. Subscriptions & Payments' },
  { id: 'cancellation', title: '7. Cancellation & Refunds' },
  { id: 'patient-obligations', title: '8. Patient Obligations' },
  { id: 'not-emergency', title: '9. Not for Emergencies' },
  { id: 'liability', title: '10. Limitation of Liability' },
  { id: 'termination', title: '11. Termination' },
  { id: 'governing-law', title: '12. Governing Law' },
  { id: 'contact', title: '13. Contact' },
];

const GLP1Terms = () => {
  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <FaFileContract className="text-2xl text-gold-400" />
          </div>
          <h1 className="text-4xl font-bold mb-3">GLP-1 Program Terms of Service</h1>
          <p className="text-gray-300">{COMPANY} · Last updated: {LAST_UPDATED}</p>
          <p className="text-gray-400 text-sm mt-2">Applies to the GLP-1 Medical Weight Loss Program at glp1.fitnession.fit</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-12">

          {/* Sticky TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block text-sm py-1.5 px-3 rounded-lg transition-colors ${
                      activeSection === s.id
                        ? 'bg-gold-50 text-gold-700 font-medium'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className="flex-1 min-w-0">

            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-10 flex gap-3">
              <FaExclamationTriangle className="text-red-500 text-xl flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 mb-1">Important: Read Before Using This Service</p>
                <p className="text-red-700 text-sm leading-relaxed">
                  This programme involves prescription medications supervised by licensed physicians. These Terms contain important information about medical limitations, your obligations as a patient, and the risks of GLP-1 medications. Please read carefully before proceeding.
                </p>
              </div>
            </div>

            <section id="acceptance" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                By accessing the GLP-1 Medical Weight Loss Program at glp1.fitnession.fit, completing an eligibility assessment, booking a consultation, or enrolling in the programme, you agree to these GLP-1 Terms of Service ("GLP-1 Terms") in addition to the general <a href="https://fitnession.fit/terms" className="text-navy-700 underline font-medium">Fitnession Terms of Service</a> and our <Link to="/privacy-policy" className="text-navy-700 underline font-medium">Privacy Policy</Link>.
              </p>
              <p className="text-gray-600 leading-relaxed mt-3">
                If you do not agree to all of these terms, you must not use the GLP-1 programme.
              </p>
            </section>

            <section id="medical-nature" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Medical Nature of the Program</h2>
              <p className="text-gray-600 mb-4">The Fitnession GLP-1 programme is a <strong>medical weight loss service</strong> that includes:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                <li>Doctor consultations via telemedicine (licensed physicians)</li>
                <li>Prescription of GLP-1 medications where clinically appropriate</li>
                <li>Ongoing medical monitoring (laboratory tests, check-ins)</li>
                <li>Prescription management and dosage adjustments</li>
              </ul>

              <div className="bg-navy-50 border border-navy-100 rounded-xl p-5 space-y-3">
                <p className="text-sm text-navy-900 font-semibold">Important limitations you must understand:</p>
                <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                  <li>Our doctors provide <strong>telemedicine consultations</strong>, not in-person examinations. Their assessments are based on the information you provide and available records.</li>
                  <li>A telemedicine consultation does not establish a traditional doctor-patient relationship for the purposes of in-person care.</li>
                  <li>Our doctors exercise independent clinical judgement. We cannot guarantee a prescription will be issued — eligibility is determined solely by the treating physician.</li>
                  <li>This service does not replace your regular GP, specialist, or emergency care provider.</li>
                </ul>
              </div>
            </section>

            <section id="eligibility" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility Requirements</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.1 Clinical Eligibility</h3>
              <p className="text-gray-600 mb-3">To be clinically eligible for this programme, you generally must:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-4">
                <li>Be aged 18–65 years</li>
                <li>Have a BMI ≥ 30, or BMI ≥ 27 with at least one weight-related health condition</li>
                <li>Not be pregnant or planning pregnancy within 6 months</li>
                <li>Not have a personal or family history of medullary thyroid carcinoma or MEN2 syndrome</li>
                <li>Not have a history of pancreatitis or severe gastrointestinal disease</li>
                <li>Not have Type 1 diabetes</li>
                <li>Not have an active eating disorder</li>
              </ul>
              <p className="text-gray-600 mb-4">Final eligibility is determined by our doctors after a full consultation. The above are general guidelines only.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.2 Information Accuracy</h3>
              <p className="text-gray-600">
                You must provide complete and accurate medical history during the eligibility assessment and consultation. Providing false or incomplete information to obtain a prescription is a <strong>serious violation</strong> of these Terms and may constitute a criminal offence under the Drugs and Cosmetics Act 1940. We reserve the right to terminate your access and report such conduct to appropriate authorities.
              </p>
            </section>

            <section id="telemedicine" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Telemedicine Consent</h2>
              <p className="text-gray-600 mb-4">By using this service, you consent to receiving medical consultations via video call (telemedicine). You acknowledge:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                <li>Telemedicine is conducted in compliance with the Telemedicine Practice Guidelines issued by the Ministry of Health and Family Welfare, India (2020).</li>
                <li>You have the right to request an in-person consultation if you prefer, in which case we may refer you to a local physician.</li>
                <li>Telemedicine has limitations — your doctor cannot conduct a physical examination. You must accurately describe all symptoms and conditions.</li>
                <li>Consultation sessions may be recorded for quality assurance and medical recordkeeping, with your consent at the time of the call.</li>
                <li>You consent to our doctors accessing and reviewing your health records, check-in data, and lab results shared via the platform.</li>
              </ul>
            </section>

            <section id="prescription" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Prescription & Medication</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 Prescription Issuance</h3>
              <p className="text-gray-600 mb-3">GLP-1 medications are prescription-only drugs in India. Prescriptions are issued solely by our licensed doctors based on clinical assessment. Fitnession (the company) does not prescribe — only the treating physician does.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Medication Supply</h3>
              <p className="text-gray-600 mb-3">Medication is dispensed by our partner licensed pharmacies based on valid prescriptions. Included in your monthly programme fee. You agree:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-4">
                <li>To use medication only as prescribed — do not alter dosage without doctor approval</li>
                <li>To store medication correctly (refrigerated, 2–8°C for unopened pens)</li>
                <li>To dispose of used needles safely (sharps containers — we provide guidance)</li>
                <li>Not to share medication with any other person</li>
                <li>To report all side effects to your assigned doctor via the platform or WhatsApp</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.3 Medication Delivery</h3>
              <p className="text-gray-600 mb-3">We target delivery within 2–4 business days. Delivery delays due to logistics, extreme weather, or supply chain issues are outside our control. In the event of delayed delivery, contact support immediately — we will prioritise resolution. We are not liable for medication degradation caused by improper handling after delivery.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.4 Off-Label Use</h3>
              <p className="text-gray-600">Some medications may be prescribed for indications beyond their primary approval (off-label use) at the doctor's clinical discretion. Your doctor will explain this during consultation if applicable.</p>
            </section>

            <section id="subscriptions" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Subscriptions & Payments</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.1 Consultation Fee</h3>
              <p className="text-gray-600 mb-3">A non-refundable* consultation fee of ₹1,000 is charged for the initial doctor consultation. *This fee is fully refunded if you enrol in the programme within 7 days of the consultation, or if you are found ineligible by our doctor.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.2 Programme Fee</h3>
              <p className="text-gray-600 mb-3">The monthly programme fee is ₹24,999/month (all-inclusive: medication, consultations, coaching, labs). A 6-month prepay option of ₹1,35,000 is available. Prices include GST where applicable.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">6.3 Billing</h3>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600">
                <li>Monthly subscriptions auto-renew unless cancelled with 30 days' notice.</li>
                <li>Payments are processed via Razorpay. We do not store card details.</li>
                <li>Price changes will be communicated 30 days in advance.</li>
              </ul>
            </section>

            <section id="cancellation" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cancellation & Refunds</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.1 Minimum Commitment</h3>
              <p className="text-gray-600 mb-3">The GLP-1 programme requires a <strong>minimum commitment of 3 months</strong>. This is a clinical requirement — meaningful and safe weight loss with GLP-1 medication requires at least 12 weeks of supervised treatment. Cancellation requests before 3 months are not accepted except in cases of medical necessity (Section 7.3).</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.2 Cancellation After 3 Months</h3>
              <p className="text-gray-600 mb-3">After completing 3 months, you may cancel with 30 days' written notice to {EMAIL}. Access and medication supply continues through the end of the notice period. No partial-month refunds.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.3 Medical Necessity Cancellation</h3>
              <p className="text-gray-600 mb-3">If your doctor (ours or an independent physician) certifies that continuing GLP-1 treatment is medically contraindicated, you may cancel before 3 months with a written medical certificate. A pro-rated refund will be issued for the unused portion of the current month minus medication and lab costs already incurred.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">7.4 6-Month Prepay Refund</h3>
              <p className="text-gray-600">The 6-month prepay is non-refundable after the first month, except in cases of medical necessity per Section 7.3.</p>
            </section>

            <section id="patient-obligations" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Patient Obligations</h2>
              <p className="text-gray-600 mb-3">As a participant in a medically supervised programme, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Attend all scheduled doctor consultations and coaching sessions.</li>
                <li>Complete weekly health check-ins on the platform honestly and promptly.</li>
                <li>Report any new symptoms, side effects, or changes to medications immediately.</li>
                <li>Follow the prescribed dosing schedule — do not self-adjust dosage.</li>
                <li>Inform your existing GP, specialists, or any other healthcare providers that you are taking GLP-1 medication.</li>
                <li>Attend all required lab tests — your safety depends on monitoring.</li>
                <li>Not purchase GLP-1 medications from any other source while enrolled in this programme without informing your doctor.</li>
              </ul>
            </section>

            <section id="not-emergency" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Not for Emergencies</h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <p className="font-bold text-red-800 mb-3">⚠️ THIS SERVICE IS NOT FOR MEDICAL EMERGENCIES</p>
                <p className="text-red-700 mb-3">If you experience any of the following, <strong>call 112 or go to your nearest emergency room immediately</strong> — do not use this platform:</p>
                <ul className="list-disc list-inside space-y-1.5 text-red-700">
                  <li>Severe abdominal pain (possible pancreatitis)</li>
                  <li>Symptoms of a stroke or heart attack</li>
                  <li>Severe allergic reaction (anaphylaxis)</li>
                  <li>Dangerously low blood sugar (confusion, unconsciousness)</li>
                  <li>Difficulty breathing</li>
                  <li>Any life-threatening emergency</li>
                </ul>
                <p className="text-red-700 mt-3 text-sm font-medium">Emergency helpline: 112 | Nearest Apollo/Fortis emergency</p>
              </div>
            </section>

            <section id="liability" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-600 mb-3">To the fullest extent permitted by Indian law:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Fitnession is not liable for any adverse medical outcomes arising from GLP-1 treatment when the treatment was prescribed and monitored in good faith by our doctors based on the information you provided.</li>
                <li>If you provide false or incomplete medical history and suffer an adverse outcome as a result, Fitnession and its doctors bear no liability.</li>
                <li>Fitnession's total financial liability for any claim shall not exceed the total fees you paid in the 3 months preceding the claim.</li>
                <li>We are not liable for weight loss results — clinical outcomes depend on individual factors outside our control, including adherence, metabolism, and concurrent conditions.</li>
                <li>Our doctors exercise independent clinical judgement — Fitnession (the company) is not liable for individual prescribing decisions made by licensed physicians in their professional capacity.</li>
              </ul>
            </section>

            <section id="termination" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination by Fitnession</h2>
              <p className="text-gray-600 mb-3">We or your assigned doctor may terminate your programme participation if:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-3">
                <li>You are found medically ineligible or a safety concern arises</li>
                <li>You provide false medical information</li>
                <li>You repeatedly miss consultations and check-ins without notice</li>
                <li>You share, resell, or misuse prescribed medication</li>
                <li>You engage in abusive behaviour toward medical or coaching staff</li>
              </ul>
              <p className="text-gray-600">In the event of medically-driven termination, a pro-rated refund for unused medication costs may be issued at our discretion.</p>
            </section>

            <section id="governing-law" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-600">These GLP-1 Terms are governed by the laws of India, including the Drugs and Cosmetics Act 1940, the Telemedicine Practice Guidelines 2020, the Consumer Protection Act 2019, and the DPDP Act 2023. Disputes shall be subject to the exclusive jurisdiction of courts in Pune, Maharashtra.</p>
            </section>

            <section id="contact" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact</h2>
              <div className="bg-gray-50 rounded-2xl p-6">
                <p className="font-bold text-gray-900 mb-1">{COMPANY}</p>
                <p className="text-gray-600 text-sm mb-3">GLP-1 Legal & Compliance</p>
                <p className="flex items-center gap-2 text-sm text-gray-600 mb-1"><FaEnvelope className="text-gold-500" /> {EMAIL}</p>
                <p className="text-sm text-gray-500">For medical concerns: contact your assigned doctor directly via the platform or WhatsApp.</p>
              </div>
            </section>

          </article>
        </div>
      </div>
    </div>
  );
};

export default GLP1Terms;
